import { StatusCodes } from "http-status-codes"
import { SetStateAction, useCallback, useEffect, useState } from "react"
import { useForm, useWatch } from "react-hook-form"
import { toast } from "react-toastify"
import { digitalManagerApi } from "../../../../../services/api"
import { renderDevicesSelectionLayout } from "../../../../components/Helpers"
import { Guid } from "../../../../models/CustomType"
import { DeviceGet } from "../../../../models/Device"
import { DeviceCategoryGet } from "../../../../models/DeviceCategory"

interface CategoryDevicesProps {
  readonly deviceCategory: DeviceCategoryGet
  readonly reloadCategoryContainer: () => void
}

const handleSave = (deviceCategoryId: Guid, checkedDevices: Guid[]) =>
  digitalManagerApi
    .post(`/deviceCategories/${deviceCategoryId}/devices`, {
      deviceIds: checkedDevices,
    })
    .then((res) => {
      if (res.status === StatusCodes.CREATED) {
        toast.success("Category's devices updated")
      }
    })
    .catch(() => {
      toast.error("Cannot update category's devices")
    })

export const CategoryDevices = ({
  deviceCategory,
  reloadCategoryContainer,
}: CategoryDevicesProps) => {
  const [devices, setDevices] = useState<DeviceGet[] | null>(null)

  const {
    handleSubmit,
    setValue,
    formState: { isDirty, isSubmitting },
    reset,
    control,
  } = useForm<{ devices: Guid[] }>({})

  const devicesValues = useWatch({ control, name: "devices" })

  useEffect(() => {
    reset({
      devices: deviceCategory.devices.map((device) => device.id),
    })
  }, [deviceCategory.devices, reset])

  const onSubmit = async (data: { devices: Guid[] }) => {
    await handleSave(deviceCategory.id, data.devices)
    reloadCategoryContainer()
  }

  useEffect(() => {
    digitalManagerApi
      .get<DeviceGet[]>("/devices")
      .then((res) => {
        setDevices(res.data)
      })
      .catch(() => {
        setDevices([])
        toast.error("Error retrieving the list of devices")
      })
  }, [])

  const onClickSelectAll = useCallback(() => {
    if (devices) {
      setValue(
        "devices",
        devices.map((d) => d.id),
        { shouldDirty: true }
      )
    }
  }, [devices, setValue])

  const onClickUnselectAll = useCallback(() => {
    setValue("devices", [], { shouldDirty: true })
  }, [setValue])

  const handleSetCheckedItemsIds = useCallback(
    (update: SetStateAction<string[]>) => {
      const value = typeof update === "function" ? update(devicesValues) : update

      setValue("devices", value, { shouldDirty: true })
    },
    [setValue, devicesValues]
  )

  return renderDevicesSelectionLayout({
    devices,
    handleSubmit,
    onSubmit,
    isDirty,
    isSubmitting,
    onClickSelectAll,
    onClickUnselectAll,
    checkedItemsIds: devicesValues,
    setCheckedItemsIds: handleSetCheckedItemsIds,
  })
}
